import dayjs, { Dayjs } from 'dayjs'
import isLeapYear from 'dayjs/plugin/isLeapYear'
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'

import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Calendar, CalendarChangeParams } from 'primereact/calendar'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { Row } from 'primereact/row'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_LEARNING_JOURNALS_SHORT } from '../../../constants'
import { JournalRythmCt, RequestMethod } from '../../../model/enums/enums'
import { learningJournal } from '../../../model/learnJournal.model'
import {
  Apprentice,
  apprenticeWithLearningJournal,
} from '../../../model/person.model'
import { Profession } from '../../../model/profession.model'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { addLearningJournal } from '../../../redux/official-document/learning-journal/learningJournalActions'
import { AuthRoutes } from '../../../router/routes'
import { handleRequest } from '../../../services/api/base/handleRequest'
import { findApprenticeById } from '../../../util/arraySearch'
import { apprenticeFilterTemplate } from '../../util/apprentice-filter/ApprenticeDataTableFilter'
import { AvatarCell } from '../../util/data-table/cell-body/FullNameApprenticeBody'
import { InactiveRowStyleByObjectAndPerson } from '../../util/data-table/row-class/InactiveRow'
import { sortProfession } from '../../util/sort-functions/TableSortFunctions'

dayjs.extend(weekOfYear)
dayjs.extend(isoWeeksInYear)
dayjs.extend(isLeapYear)
dayjs.extend(weekday)

interface FilterProps {
  showOnlyActiveApprenticeship?: boolean
  selectedGroupId?: number
  selectProfessionId?: number
}

interface DateStorage {
  date: Dayjs[]
  month: number
}

export const JournalList = (): JSX.Element => {
  const numberValues = [...Array(11).keys()].slice(1).map((val, idx) => {
    return `${val.toString()} 
   ${
     idx > 0
       ? t<string>('journal.weeks.plural')
       : t<string>('journal.weeks.singular')
   }`
  })

  // we filter the ones without apprentice body
  const apprentices = useAppSelector((state) =>
    state.apprentices.filter((appr: Apprentice) => {
      return appr.apprentice
    })
  )

  const activeLanguage = localStorage.getItem('i18nextLng')
  const professions = useAppSelector((state) => state.professions)

  const navigate = useNavigate()

  const [filterOptions, setFilterOptions] = useState<FilterProps>()
  const [selectedProfession, setSelectedProfession] = useState<number>()
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true)
  const [selectedGroup, setSelectedGroup] = useState<number>()
  const [
    allLearningJournalsWithApprentices,
    setAllLearningJournalsWithApprentices,
  ] = useState<apprenticeWithLearningJournal[]>([])

  const [chosenWeek, setChoosenWeek] = useState<Dayjs>(dayjs())
  const [weeksShown, setWeeksShown] = useState<number>(4)
  const dispatcher = useAppDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    setFilterOptions({
      showOnlyActiveApprenticeship: showOnlyActive,
      selectedGroupId: selectedGroup,
      selectProfessionId: selectedProfession,
    })
  }, [selectedGroup, showOnlyActive, selectedProfession])

  useEffect(() => {
    let filteredObjects = [...allLearningJournalsWithApprentices]
    if (filterOptions?.showOnlyActiveApprenticeship) {
      filteredObjects = filteredObjects.filter(
        (lj) => lj.is_active && isActive(lj, chosenWeek)
      )
    }
    if (filterOptions?.selectedGroupId) {
      filteredObjects = filterGroup(
        filteredObjects,
        filterOptions.selectedGroupId
      )
    }

    if (filterOptions?.selectProfessionId) {
      filteredObjects = filteredObjects.filter(
        (lj) => lj.apprentice?.id_profession === selectedProfession
      )
    }

    setFilteredLearningJournals(filteredObjects)
  }, [filterOptions])

  const isActive = (apprentice: Apprentice, d: Dayjs): boolean => {
    if (
      apprentice.apprentice &&
      apprentice.apprentice.contract_start &&
      apprentice.apprentice.contract_end
    ) {
      return (
        dayjs(apprentice.apprentice.contract_start).isBefore(d, 'week') &&
        dayjs(apprentice.apprentice.contract_end).isAfter(d, 'week')
      )
    } else {
      return false
    }
  }
  const groups = useAppSelector((state) => state.groups)

  const learningJournals = useAppSelector((state) => state.learningJournals)

  const weeksInTable = Array.from({ length: weeksShown }, (val, idx) => {
    return chosenWeek.subtract(weeksShown - idx - 1, 'week')
  })

  const apprenticeAvatar = (apprentice: Apprentice) => {
    const app = findApprenticeById(
      apprentice.apprentice?.apprentice_id,
      apprentices
    )
    return AvatarCell(app)
  }

  const { loadedBasicInformation } = useAppSelector((state) => state.user)

  const [filteredLearningJournals, setFilteredLearningJournals] = useState<
    apprenticeWithLearningJournal[]
  >([])

  const [date, setDate] = useState<string>()

  const fetchFromDb = async () => {
    setIsLoading(true)
    const result = await handleRequest<learningJournal[]>(
      API_LEARNING_JOURNALS_SHORT(),
      RequestMethod.GET
    )

    if (!result.data || result.error) {
      console.warn('[Journal List] Could fetch the journals, an error ocurred')
      return
    }
    const journals = result.data

    const apprWithJournal = combineApprenticesAndJournal(journals, true)
    const apprWithJournalsFiltered = apprWithJournal.filter(
      (val) => val.is_active && isActive(val, chosenWeek)
    )
    setFilteredLearningJournals(apprWithJournalsFiltered)
    setAllLearningJournalsWithApprentices(apprWithJournal)
    setIsLoading(false)
  }

  const combineApprenticesAndJournal = (
    journals: learningJournal[],
    dispatch: boolean
  ) => {
    return apprentices.map((val: Apprentice): apprenticeWithLearningJournal => {
      const correspondingEntries = journals?.filter(
        (journal) => journal.id_apprentice === val.apprentice?.apprentice_id
      )
      if (!correspondingEntries) {
        return {
          ...val,
          learningJournals: [],
        }
      }
      correspondingEntries.forEach((entry) => {
        if (dispatch) {
          dispatcher(addLearningJournal(entry))
        }
      })
      return {
        ...val,
        learningJournals: correspondingEntries,
      }
    })
  }

  useEffect(() => {
    if (loadedBasicInformation && !isLoading) {
      if (learningJournals.length === 0) {
        fetchFromDb()
        return
      }
      const apprWithJournal = combineApprenticesAndJournal(
        learningJournals,
        false
      )
      const apprWithJournalsFiltered = apprWithJournal.filter(
        (val) => val.is_active && isActive(val, chosenWeek)
      )
      setFilteredLearningJournals(apprWithJournalsFiltered)
      setAllLearningJournalsWithApprentices(apprWithJournal)
    }
  }, [loadedBasicInformation, learningJournals])

  const routeToNewJournal = (d: Dayjs, apprenticeId: any) => {
    // should never happen
    if (!apprenticeId) {
      console.warn('[Journal List] an apprentice displayed does not exist')
      return
    }
    navigate(AuthRoutes.addJournal, {
      state: {
        // this is a hack, not sure why we cannot pass the state object to the route here
        date: d.toString(),
        apprentice_id: apprenticeId,
      },
    })
  }

  const routeToExistingJournal = (jounralId: any) => {
    // should never happen
    if (!jounralId) {
      console.warn('[Journal List] a Journal is displayed that does not exist')
      return
    }
    navigate(`${AuthRoutes.journals}/${jounralId}`)
  }

  const getWeekInformation = (
    app: apprenticeWithLearningJournal,
    weekDate: Dayjs
  ): JSX.Element => {
    const ljww = app.learningJournals
    const weekJournal = ljww.find((val) =>
      dayjs(val.entry_date).isSame(weekDate, 'week')
    )
    let styleClass = ''
    const weekOrMonth =
      app.apprentice?.journal_start === JournalRythmCt.Monthly
        ? 'month'
        : 'week'
    const firstWeekOfPreviousMonth = weekDate.weekday(0).endOf('month')
    const firstWeekOfNextMonth = weekDate.weekday(6).endOf('month')
    const monthOfFirstDayinWeek = weekDate.weekday(0)
    let actionElement

    if (app.apprentice?.journal_rythm === JournalRythmCt.Monthly) {
      styleClass =
        monthOfFirstDayinWeek.month() % 2
          ? 'month-cell-even'
          : 'month-cell-uneven'
    }

    if (!app.is_active) {
      styleClass = 'inactive-cell'
    }
    if (!isActive(app, weekDate)) {
      actionElement = <></>
      styleClass = 'inactive-cell'
    } else if (
      app.apprentice?.journal_start &&
      dayjs(app.apprentice.journal_start).isAfter(weekDate, weekOrMonth)
    ) {
      actionElement = <></>
      styleClass = 'inactive-cell'
    } else if (!weekJournal) {
      const isInPast = weekDate.isBefore(dayjs(), 'week')
      const icon = isInPast ? 'icon-red' : ''

      if (app.apprentice?.journal_rythm !== JournalRythmCt.Monthly) {
        actionElement = (
          <Button
            className={`p-button-text ${icon}`}
            icon={'fa-solid fa-plus-circle'}
            tooltip={t<string>('journal.symbols.missing')}
            tooltipOptions={{ style: { minWidth: '100px' }, showDelay: 500 }}
            onClick={() =>
              routeToNewJournal(weekDate, app.apprentice?.apprentice_id)
            }
          />
        )
      } else {
        if (
          firstWeekOfPreviousMonth.isSame(weekDate, 'week') ||
          firstWeekOfNextMonth.isSame(weekDate, 'week')
        ) {
          actionElement = (
            <Button
              className={`p-button-text ${icon}`}
              icon={'fa-solid fa-plus-circle'}
              tooltip={t<string>('journal.symbols.missing')}
              tooltipOptions={{ style: { minWidth: '100px' }, showDelay: 500 }}
              onClick={() =>
                routeToNewJournal(weekDate, app.apprentice?.apprentice_id)
              }
            />
          )
        } else {
          actionElement = <></>
        }
      }
    } else if (weekJournal.completed) {
      actionElement = (
        <Button
          className={'p-button-text icon-green'}
          onClick={() =>
            routeToExistingJournal(weekJournal.learning_journal_id)
          }
          tooltip={t<string>('journal.symbols.completed')}
          tooltipOptions={{ style: { minWidth: '100px' }, showDelay: 500 }}
          icon={'fa-solid fa-check-circle'}
        />
      )
    } else if (!weekJournal.is_locked) {
      actionElement = (
        <Button
          className={'p-button-text icon-blue'}
          tooltipOptions={{ style: { minWidth: '100px' }, showDelay: 500 }}
          onClick={() =>
            routeToExistingJournal(weekJournal.learning_journal_id)
          }
          icon={'fa-solid fa-pen'}
          tooltip={t<string>('journal.symbols.edit')}
        />
      )
    } else {
      actionElement = (
        <Button
          className={'p-button-text icon-orange'}
          tooltipOptions={{ style: { minWidth: '100px' }, showDelay: 500 }}
          onClick={() =>
            routeToExistingJournal(weekJournal.learning_journal_id)
          }
          icon={'fa-solid fa-eye'}
          tooltip={t<string>('journal.symbols.review')}
        />
      )
    }

    return (
      <div className={styleClass}>
        {' '}
        &nbsp;
        {actionElement}
      </div>
    )
  }
  const headerGroup = () => {
    const weeks = Array.from({ length: weeksShown }, (val, idx) => {
      return chosenWeek.subtract(weeksShown - idx - 1, 'week')
    })

    let dateStorer: DateStorage[] = []
    let idx = 0
    for (let weekDate of weeks) {
      const month = weekDate.weekday(0).month()

      if (!dateStorer[idx]) {
        dateStorer[idx] = {
          month: month,
          date: [weekDate],
        }
      } else if (dateStorer[idx].month !== month) {
        dateStorer.push({ month: month, date: [weekDate] })
        idx += 1
      } else if (dateStorer[idx].month === month) {
        dateStorer[idx].date.push(weekDate)
      }
    }

    return (
      <ColumnGroup>
        <Row>
          <Column
            header={t<string>('core.apprentices')}
            colSpan={1}
            rowSpan={2}
          ></Column>
          <Column
            header={t<string>('core.field_of_profession')}
            colSpan={1}
            rowSpan={2}
          ></Column>
          {dateStorer.map((ds, index) => {
            return (
              <Column
                key={index}
                header={`${t<string>(
                  `locales.monthNames.${ds.month}`
                )}  ${ds.date[0].year()}`}
                colSpan={ds.date.length}
                align={'center'}
              ></Column>
            )
          })}
        </Row>
        <Row>{weekHeader()}</Row>
      </ColumnGroup>
    )
  }

  const [currentWeek] = useState(dayjs().week())
  const weekHeader = () => {
    return weeksInTable.map((val, idx) => {
      const weekBegin = val.weekday(0).format(t<string>('locales.dateFormat'))
      const weekEnd = val.weekday(6).format(t<string>('locales.dateFormat'))

      const currentWeekIcon = 'fa-solid fa-star icon-orange ml-2'
      const isActiveWeek = val.week() === currentWeek
      return (
        <Column
          colSpan={1}
          key={`weHe_${idx}`}
          align={'center'}
          header={
            <>
              {t<string>('core.calendary_week.short')} {val.week()}
              {isActiveWeek && <span className={currentWeekIcon} />}
            </>
          }
          headerTooltip={`${weekBegin} - ${weekEnd}`}
          headerTooltipOptions={{ position: 'bottom' }}
        ></Column>
      )
    })
  }

  const weekColumns = () => {
    return weeksInTable.map((val, idx) => {
      let footer =
        idx === 0
          ? scrollLeft
          : idx === weeksInTable.length - 1
          ? scrollRight
          : null
      if (weeksInTable.length === 1) {
        footer = () => (
          <>
            {scrollLeft()} {scrollRight()}
          </>
        )
      }

      return (
        <Column
          key={idx}
          body={(app: apprenticeWithLearningJournal) =>
            getWeekInformation(app, val)
          }
          footer={footer}
          align={'center'}
          alignHeader={'center'}
          style={{ position: 'relative' }}
          // bodyClassName={val.week() === currentWeek ? 'active-column' : ''}
        />
      )
    })
  }

  const goLeft = () => {
    const newWeek = chosenWeek.subtract(weeksShown, 'week')
    setChoosenWeek(newWeek)
  }

  const goRight = () => {
    const newWeek = chosenWeek.add(weeksShown, 'week')
    setChoosenWeek(newWeek)
  }

  const scrollLeft = () => {
    return (
      <Button
        onClick={goLeft}
        tooltip={t<string>('journal.earlier')}
        className="p-button-text"
        icon="fa-solid fa-angle-left "
      ></Button>
    )
  }

  const scrollRight = () => {
    return (
      <Button
        onClick={goRight}
        tooltip={t<string>('journal.later')}
        className="p-button-text"
        icon="fa-solid fa-angle-right "
      ></Button>
    )
  }

  const calendaryChange = (c: CalendarChangeParams) => {
    const newDate = c.value?.toString()
    const week = dayjs(newDate)
    setChoosenWeek(week)
    // not sure how we can make ts happy here
    // @ts-ignore
    setDate(c.value)
  }

  const filterGroup = (
    filteredJournals: apprenticeWithLearningJournal[],
    groupId: number
  ) => {
    const groupMembers = groups.find(
      (g) => groupId === g.group_id
    )?.group_member
    const groupMemberIds = groupMembers?.map((member) => member.id_person)
    const peopleInGroup = filteredJournals.filter((lj) => {
      if (lj.person_id) {
        return groupMemberIds?.includes(lj.person_id)
      }
    })

    return peopleInGroup
  }

  const getProfessionLabel = (body: any) => {
    const prof = professions.find(
      (p: Profession) => body.apprentice?.id_profession === p.profession_id
    )
    if (prof) {
      return activeLanguage !== 'de'
        ? t(`profession.${prof.profession_id}`)
        : prof.profession
    }
    return ''
  }

  const resetFilter = () => {
    setSelectedGroup(undefined)
    setSelectedProfession(undefined)
    setDate(new Date().toString())
    setWeeksShown(4)
  }

  const professionsDropdownOptions = () => {
    let profs = professions?.map((p: Profession) => {
      return {
        label:
          activeLanguage === 'de'
            ? p.profession
            : t(`profession.${p.profession_id}`),
        value: p.profession_id,
      }
    })

    profs.unshift({
      label: t('profession.all_professions'),
      value: -1,
    })
    return profs
  }

  return (
    <div className={'card pr-2'}>
      <div
        className={
          'grid flex-wrap w-full justify-content-between align-items-center pr-2'
        }
      >
        <div className="field lg:col-3 md:col-3 col-12">
          <Button
            label={t('journal.open_issues')}
            icon={'fa-solid fa-clock'}
            onClick={() => navigate(AuthRoutes.listJournals)}
            className={'p-button-raised ml-2'}
          />
        </div>
        <div className="field col">
          <Dropdown
            value={showOnlyActive}
            options={[
              { value: false, label: t('core.show_all') },
              {
                value: true,
                label: t('core.show_active'),
              },
            ]}
            onChange={(e) => setShowOnlyActive(!showOnlyActive)}
          />
        </div>
        <div className="field col">
          <Dropdown
            value={selectedProfession}
            options={professionsDropdownOptions()}
            placeholder={t('education_report.show_all_professions')}
            onChange={(e) => setSelectedProfession(e.target.value)}
            filter
            showFilterClear={true}
            showClear
            className={'max-w-10rem'}
          />
        </div>
        <div className="field col">
          <Dropdown
            onChange={(e) => setSelectedGroup(e.target.value?.group_id)}
            placeholder={t<string>('hrm.group.all')}
            value={groups.find((g) => g.group_id === selectedGroup)}
            options={groups}
            showFilterClear={true}
            optionLabel="group_name"
            filter
            filterBy="group_name"
            showClear
            className={'max-w-10rem'}
          />
        </div>
        <div className="field col">
          <Dropdown
            options={numberValues}
            defaultValue={t<string>('journal.num_weeks')}
            placeholder={t<string>('journal.num_weeks')}
            onChange={(e) => {
              setWeeksShown(parseInt(e.value))
            }}
            value={numberValues[weeksShown - 1]}
          />
        </div>
        <div className="field col">
          <Calendar
            className={'min-width-240'}
            value={date}
            dateFormat={'dd.mm.yy'}
            showIcon
            showWeek
            placeholder={t<string>('journal.move_date')}
            onChange={calendaryChange}
          />
        </div>
        <div className="field col max-w-2rem">
          <Button
            icon={'pi pi-filter-slash'}
            className={'p-button-text'}
            tooltip={t<string>('core.reset_filter')}
            tooltipOptions={{ position: 'left' }}
            onClick={() => resetFilter()}
          />
        </div>
      </div>
      <DataTable
        showGridlines
        stripedRows
        className={'p-datatable-sm ml-0 pl-0'}
        value={filteredLearningJournals}
        rowHover
        dataKey={'person_id'}
        sortField={'full_name'}
        sortOrder={1}
        sortMode="single"
        removableSort
        scrollable
        scrollHeight={'calc(100vh - 320px)'}
        emptyMessage={t<string>('core.empty_message')}
        rowClassName={(row: apprenticeWithLearningJournal) =>
          InactiveRowStyleByObjectAndPerson(
            row,
            row.apprentice?.apprentice_id,
            apprentices
          )
        }
        headerColumnGroup={headerGroup()}
      >
        <Column
          field={'full_name'}
          filterField={'full_name'}
          body={(val: apprenticeWithLearningJournal) => apprenticeAvatar(val)}
          sortable
          filter
          filterElement={(e) => apprenticeFilterTemplate(e, apprentices)}
          filterPlaceholder={t<string>('core.all')}
          showFilterMenu={false}
        />
        <Column
          field={'apprentice.id_profession'}
          filterField={'apprentice.id_profession'}
          body={(body: apprenticeWithLearningJournal) =>
            getProfessionLabel(body)
          }
          sortable
          sortFunction={(e) => sortProfession(e, professions)}
        />
        {weekColumns()}
      </DataTable>
      <div className="flex mt-2 mb-2 w-full justify-content-end">
        <Button
          label={t<string>('journal.create')}
          icon={'fa-solid fa-plus'}
          onClick={() => navigate(AuthRoutes.addJournal)}
          className={'mb-2 p-button-raised'}
        />
      </div>
    </div>
  )
}